/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Users, Send, Stethoscope, Heart, HandHelping, Sparkles, Loader2, UserPlus, Trash2, Calendar as CalIcon, MessageSquare, Bandage } from 'lucide-react';
import {
  listMyCareTeams, createCareTeam, inviteCareMember, removeCareMember,
  addCareNote, getCareTimeline,
} from '@/lib/sprint4';
import { useAuth } from '@/lib/auth';
import { CareAiPanel } from '@/components/care/CareAiPanel';

const ROLE_META = {
  medico: { label: 'Medico', Icon: Stethoscope, cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  infermiere: { label: 'Infermiere', Icon: Heart, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  oss: { label: 'OSS', Icon: HandHelping, cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  paziente: { label: 'Paziente', Icon: Users, cls: 'bg-slate-100 text-slate-800 border-slate-200' },
};

const KIND_META = {
  note: { label: 'Nota', cls: 'bg-slate-100 text-slate-800' },
  handoff: { label: 'Handoff', cls: 'bg-blue-100 text-blue-800' },
  red_flag: { label: 'Red flag', cls: 'bg-red-100 text-red-800' },
  question: { label: 'Domanda', cls: 'bg-amber-100 text-amber-800' },
  update: { label: 'Aggiornamento', cls: 'bg-emerald-100 text-emerald-800' },
};

const CareTeamPage = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState(null);
  const [team, setTeam] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingTl, setLoadingTl] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteKind, setNoteKind] = useState('note');
  const [inviteId, setInviteId] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [busy, setBusy] = useState(false);

  const isPatient = user?.role === 'patient' || user?.category === 'paziente';

  const load = useCallback(async () => {
    try {
      const res = await listMyCareTeams();
      setTeams(res.teams || []);
      if ((res.teams || []).length > 0) setTeam(res.teams[0]);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Errore caricamento team');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadTimeline = useCallback(async () => {
    if (!team) return;
    setLoadingTl(true);
    try {
      const res = await getCareTimeline(team.id);
      setEvents(res.events || []);
    } catch (e) {
      toast.error('Errore timeline');
    } finally {
      setLoadingTl(false);
    }
  }, [team]);
  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  const createNew = async () => {
    setBusy(true);
    try {
      const res = await createCareTeam({ label: 'Il mio team di cura' });
      setTeam(res.team);
      setTeams([res.team]);
      toast.success(res.already_exists ? 'Team già attivo' : 'Team creato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const invite = async () => {
    if (!inviteId.trim()) return;
    setBusy(true);
    try {
      await inviteCareMember(team.id, inviteId.trim(), inviteRole.trim() || undefined);
      toast.success('Membro aggiunto');
      setInviteId(''); setInviteRole('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore invito');
    } finally { setBusy(false); }
  };

  const [acting, setActing] = useState(false);
  const remove = async (uid) => {
    if (acting || !window.confirm('Rimuovere questo membro dal team?')) return;
    setActing(true);
    try {
      await removeCareMember(team.id, uid);
      toast.success('Membro rimosso');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setActing(false); }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await addCareNote(team.id, { text: noteText.trim(), kind: noteKind });
      setNoteText('');
      await loadTimeline();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setBusy(false); }
  };

  const membersList = useMemo(() => (team?.members || []), [team]);

  if (teams === null) return <div className="p-6"><Skeleton className="h-40 w-full" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="care-team-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6 text-primary" /> Team di cura
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Un team multidisciplinare (medico + infermiere + OSS) che collabora sul tuo percorso di salute. Timeline condivisa, note e handoff strutturati.
        </p>
      </div>

      {teams.length === 0 && isPatient && (
        <Card><CardContent className="p-8 text-center space-y-3">
          <Users className="h-10 w-10 text-primary mx-auto" />
          <div className="text-sm">Non hai ancora un team di cura. Creane uno per invitare i professionisti che ti seguono.</div>
          <Button onClick={createNew} disabled={busy} data-testid="care-create-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Crea il mio team
          </Button>
        </CardContent></Card>
      )}

      {teams.length === 0 && !isPatient && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          Non fai parte di alcun team di cura. Attendi l'invito di un paziente.
        </CardContent></Card>
      )}

      {team && (
        <>
          <Card data-testid="care-team-header">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-display font-semibold">{team.label}</div>
                <div className="text-xs text-muted-foreground">{membersList.length} membri</div>
              </div>
            </CardContent>
          </Card>

          {/* Members */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg">Membri del team</h2>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
              {membersList.map((m) => {
                const meta = ROLE_META[m.role] || ROLE_META.medico;
                const RIcon = meta.Icon;
                return (
                  <Card key={m.user_id} data-testid={`care-member-${m.user_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center"><RIcon className="h-4 w-4 text-primary" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{m.role_note || meta.label}</div>
                        <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                      </div>
                      {isPatient && m.role !== 'paziente' && (
                        <Button variant="ghost" size="icon" onClick={() => remove(m.user_id)} disabled={acting} aria-label="Rimuovi membro" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {isPatient && (
              <Card><CardContent className="p-3 space-y-2">
                <div className="text-xs uppercase font-semibold text-muted-foreground">Aggiungi membro (User ID)</div>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Input value={inviteId} onChange={(e) => setInviteId(e.target.value)} placeholder="user_id del professionista" data-testid="care-invite-id-input" />
                  <Input value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} placeholder="Ruolo nel team (es. cardiologo)" data-testid="care-invite-role-input" />
                  <Button onClick={invite} disabled={busy || !inviteId.trim()} data-testid="care-invite-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Invita
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Chiedi al professionista il proprio ID (visibile nel suo profilo).</p>
              </CardContent></Card>
            )}
          </section>

          {/* AI Care Insights (Fase 23) */}
          <CareAiPanel teamId={team.id} isPatient={isPatient} />

          {/* Note composer */}
          <section className="space-y-3">
            <h2 className="font-display font-semibold text-lg">Aggiungi nota / handoff</h2>
            <Card><CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Select value={noteKind} onValueChange={setNoteKind}>
                  <SelectTrigger className="w-40" data-testid="care-note-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(KIND_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-[11px] text-muted-foreground">Tutti i membri riceveranno la nota nella timeline.</span>
              </div>
              <Textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Scrivi la tua nota... es. “PA 145/95 questa mattina, aumentato ramipril a 5 mg”" data-testid="care-note-input" />
              <div className="flex justify-end">
                <Button onClick={submitNote} disabled={busy || !noteText.trim()} data-testid="care-note-send-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia al team
                </Button>
              </div>
            </CardContent></Card>
          </section>

          {/* Timeline */}
          <section className="space-y-3">
            <h2 className="font-display font-semibold text-lg">Timeline unificata</h2>
            {loadingTl ? <Skeleton className="h-40" /> : events.length === 0 ? (
              <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Nessun evento ancora nel team.</CardContent></Card>
            ) : (
              <div className="space-y-2" data-testid="care-timeline">
                {events.map((e, i) => {
                  const key = `${e.event_type}-${e.id || e.case_id || i}`;
                  if (e.event_type === 'note') {
                    const k = KIND_META[e.kind] || KIND_META.note;
                    return (
                      <Card key={key} data-testid={`care-event-note-${e.id}`}><CardContent className="p-3">
                        <div className="flex items-center gap-2 text-xs mb-1">
                          <MessageSquare className="h-3.5 w-3.5 text-primary" />
                          <Badge variant="outline" className={`text-[10px] ${k.cls}`}>{k.label}</Badge>
                          <span className="text-muted-foreground">{new Date(e.ts).toLocaleString('it')}</span>
                          <Badge variant="outline" className="text-[10px] ml-auto">{(ROLE_META[e.author_role] || ROLE_META.medico).label}</Badge>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{e.text}</p>
                      </CardContent></Card>
                    );
                  }
                  if (e.event_type === 'appointment') {
                    return (
                      <Card key={key} data-testid={`care-event-appt-${e.id}`}><CardContent className="p-3 flex items-center gap-2">
                        <CalIcon className="h-4 w-4 text-primary" />
                        <div className="flex-1"><div className="text-sm font-semibold">Visita {e.status}</div>
                          <div className="text-[11px] text-muted-foreground">{new Date(e.ts).toLocaleString('it')}</div></div>
                      </CardContent></Card>
                    );
                  }
                  if (e.event_type === 'wound_case') {
                    return (
                      <Card key={key} data-testid={`care-event-wound-${e.case_id}`}><CardContent className="p-3 flex items-center gap-2">
                        <Bandage className="h-4 w-4 text-primary" />
                        <div className="flex-1"><div className="text-sm font-semibold capitalize">Wound case — {e.body_region}</div>
                          <div className="text-[11px] text-muted-foreground">{e.photos_count} foto · {e.status} · {new Date(e.ts).toLocaleDateString('it')}</div></div>
                      </CardContent></Card>
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default CareTeamPage;
